/**
 * The two-letter codes flag-icons ships a flag for.
 *
 * The package holds 271 files per format, but only these 257 are two-letter
 * codes: the rest are subdivisions (`gb-eng`, `es-ct`) that no GeoIP answer of
 * ours can be, since migration 0043 constrains the column to `^[A-Z]{2}$`.
 * `xx` is in here because the package ships it — it is their grey "unknown
 * country" plate rather than a country, and it only ever renders if something
 * writes the unassigned code XX into the column.
 *
 * It is a literal rather than a read of the package's own `country.json`,
 * because that file is 52 KB of capitals and continents and the only question
 * asked of it here is yes/no. flagCodes.test.ts holds this list to the
 * installed package, so an upgrade that adds or drops a flag fails the suite
 * instead of quietly changing what the panel draws.
 */
const FLAG_CODES = new Set(
  (
    "ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg " +
    "bh bi bj bl bm bn bo bq br bs bt bv bw by bz ca cc cd cf cg ch ci " +
    "ck cl cm cn co cp cr cu cv cw cx cy cz de dg dj dk dm do dz ec ee " +
    "eg eh er es et eu fi fj fk fm fo fr ga gb gd ge gf gg gh gi gl gm " +
    "gn gp gq gr gs gt gu gw gy hk hm hn hr ht hu ic id ie il im in io " +
    "iq ir is it je jm jo jp ke kg kh ki km kn kp kr kw ky kz la lb lc " +
    "li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq " +
    "mr ms mt mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om " +
    "pa pc pe pf pg ph pk pl pm pn pr ps pt pw py qa re ro rs ru rw sa " +
    "sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz tc td " +
    "tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug um un us uy uz va " +
    "vc ve vg vi vn vu wf ws xk xx ye yt za zm zw"
  ).split(" "),
);

/**
 * Does flag-icons draw this country? A code it does not know gets no flag at
 * all — an empty bordered box reads as a broken image, and the code alone still
 * answers where the player is from.
 */
export function hasFlag(code: string): boolean {
  return FLAG_CODES.has(code.toLowerCase());
}

/**
 * The same codes, upper-cased and sorted — the option list of a country
 * picker.
 *
 * This is the only alpha-2 list in the repository, and it is here rather than
 * copied into a form because a second list would be a second thing to keep in
 * step with the package: a picker offering a code this file has never heard of
 * would draw a row with no flag beside it. Callers that need real COUNTRIES
 * filter what `Intl.DisplayNames` refuses to name — a handful of these entries
 * (`EU`, `XX`) are plates the package ships rather than places.
 *
 * Built once, at module load: the set behind it is frozen at 257 entries by a
 * literal three lines up, so re-allocating and re-sorting it per call would be
 * 257 comparisons to arrive at the same array.
 */
const FLAG_CODE_LIST: readonly string[] = Object.freeze(
  [...FLAG_CODES].map((c) => c.toUpperCase()).sort(),
);

export function flagCodeList(): readonly string[] {
  return FLAG_CODE_LIST;
}
